import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Lock, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Side-by-side plan comparison generated from the backend plan matrix (single source of truth).
export const PlanComparisonTable = ({ plans: livePlans = [], currentCode, onUpgrade, subscribing }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/plan/comparison`).then(r => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return null;
  const names = Object.fromEntries(data.plans.map(p => [p.code, p.name]));
  const categories = [...new Set(data.rows.map(r => r.category))];

  const cell = (row, code) => {
    const v = row.plans[code];
    if (typeof v === 'string') return <span className="text-sm font-semibold text-stone-800">{v}</span>;
    if (v) return <Check size={16} className="mx-auto text-emerald-600" data-testid={`cmp-check-${row.capability}-${code}`} />;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" data-testid={`cmp-lock-${row.capability}-${code}`}><Lock size={14} className="mx-auto text-stone-300" /></span>
        </TooltipTrigger>
        <TooltipContent>Unlocks at {names[row.unlocks_at]}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={100}>
      <section className="mt-12 rounded-3xl border border-white/70 bg-white/60 backdrop-blur-xl shadow-xl overflow-hidden" data-testid="plan-comparison-section">
        <div className="px-6 pt-6 pb-3">
          <h2 className="text-lg font-semibold text-stone-900">Plan Comparison</h2>
          <p className="text-sm text-stone-500">Everything each plan includes. Hover a lock to see which plan unlocks it.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="sticky top-0 bg-white/80 backdrop-blur-xl">
              <tr className="border-b border-stone-200">
                <th className="text-left px-6 py-3 w-[34%] text-xs uppercase tracking-wider text-stone-500">Feature</th>
                {data.plans.map(p => {
                  const live = livePlans.find(lp => lp.code === p.code);
                  return (
                    <th key={p.code} className={`px-3 py-3 text-center ${currentCode === p.code ? 'bg-emerald-50/70' : ''}`}>
                      <div className="font-semibold text-stone-900">{p.name}</div>
                      <div className="text-xs text-stone-500">{p.amount ? `₹${p.amount.toLocaleString('en-IN')}/mo` : '₹0/mo'}</div>
                      {p.code !== 'free' && (
                        currentCode === p.code ? (
                          <span className="mt-2 inline-block text-[11px] font-semibold text-emerald-700">Current plan</span>
                        ) : (
                          <button
                            type="button"
                            disabled={!live || subscribing === p.code}
                            onClick={() => live && onUpgrade(live)}
                            className="mt-2 inline-flex items-center gap-1 rounded-full bg-stone-900 px-3 py-1 text-[11px] font-semibold text-white hover:bg-stone-700 disabled:opacity-40 transition-colors"
                            data-testid={`cmp-upgrade-${p.code}`}
                          >
                            {subscribing === p.code && <Loader2 size={11} className="animate-spin" />}Upgrade
                          </button>
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <Fragment key={cat}>
                  <tr className="bg-stone-100/70">
                    <td colSpan={data.plans.length + 1} className="px-6 py-2 text-[11px] font-semibold uppercase tracking-widest text-stone-500">{cat}</td>
                  </tr>
                  {data.rows.filter(r => r.category === cat).map(r => (
                    <tr key={`${cat}-${r.label}`} className="border-b border-stone-100 hover:bg-white/60 transition-colors">
                      <td className="px-6 py-2.5 text-stone-700">{r.label}</td>
                      {data.plans.map(p => (
                        <td key={p.code} className={`px-3 py-2.5 text-center ${currentCode === p.code ? 'bg-emerald-50/40' : ''}`}>{cell(r, p.code)}</td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </TooltipProvider>
  );
};
